import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaNewspaper, FaBox, FaChartLine, FaSignOutAlt, FaEdit, FaTrash, FaEye, FaPlus, FaChartBar, FaUsers, FaCog, FaEnvelope, FaFileExcel } from 'react-icons/fa';
import { BarChart, Bar, PieChart, Pie, LineChart, Line, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import * as XLSX from 'xlsx';
import type { News } from '../api/types';
import { getNews, deleteNews as deleteNewsApi } from '../api/news';
import { getAnalyticsOverview, type AnalyticsOverview } from '../api/analytics';
import { getAdminEmails, addAdminEmail, removeAdminEmail } from '../api/settings';
import { getAllInquiries, updateInquiryStatus, deleteInquiry, type ContactInquiry } from '../api/contact';
import NewsEditor from '../components/NewsEditor';

const Container = styled.div`
  min-height: 100vh;
  background: ${props => props.theme.colors.background};
  display: flex;
`;

const Sidebar = styled.div`
  width: 250px;
  background: linear-gradient(135deg, ${props => props.theme.colors.primary}, ${props => props.theme.colors.accent});
  color: white;
  padding: ${props => props.theme.spacing.xl};
  position: fixed;
  height: 100vh;
  left: 0;
  top: 0;

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    width: 200px;
  }
`;

const Logo = styled.div`
  font-size: 1.5rem;
  font-weight: 700;
  margin-bottom: ${props => props.theme.spacing.xxl};
  padding-bottom: ${props => props.theme.spacing.lg};
  border-bottom: 1px solid rgba(255, 255, 255, 0.2);
`;

const NavMenu = styled.nav`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.sm};
`;

const NavItem = styled.button<{ $active?: boolean }>`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.md};
  padding: ${props => props.theme.spacing.md};
  background: ${props => props.$active ? 'rgba(255, 255, 255, 0.2)' : 'transparent'};
  color: white;
  border: none;
  border-radius: ${props => props.theme.borderRadius.md};
  font-size: 1rem;
  cursor: pointer;
  transition: all ${props => props.theme.transitions.fast};
  text-align: left;

  &:hover {
    background: rgba(255, 255, 255, 0.15);
  }
`;

const LogoutButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.md};
  padding: ${props => props.theme.spacing.md};
  background: rgba(255, 255, 255, 0.1);
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: ${props => props.theme.borderRadius.md};
  font-size: 1rem;
  cursor: pointer;
  margin-top: auto;
  transition: all ${props => props.theme.transitions.fast};

  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }
`;

const MainContent = styled.div`
  flex: 1;
  margin-left: 250px;
  padding: ${props => props.theme.spacing.xxl};

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    margin-left: 200px;
    padding: ${props => props.theme.spacing.lg};
  }
`;

const Header = styled.div`
  margin-bottom: ${props => props.theme.spacing.xxl};
`;

const Title = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  color: ${props => props.theme.colors.text};
  margin-bottom: ${props => props.theme.spacing.sm};
`;

const Subtitle = styled.p`
  color: ${props => props.theme.colors.textLight};
  font-size: 1.1rem;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: ${props => props.theme.spacing.lg};
  margin-bottom: ${props => props.theme.spacing.xxl};
`;

const StatCard = styled.div`
  background: white;
  padding: ${props => props.theme.spacing.xl};
  border-radius: ${props => props.theme.borderRadius.lg};
  box-shadow: ${props => props.theme.shadows.md};
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.lg};
`;

const StatIcon = styled.div<{ $color: string }>`
  width: 60px;
  height: 60px;
  background: ${props => props.$color};
  border-radius: ${props => props.theme.borderRadius.md};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  color: white;
`;

const StatInfo = styled.div`
  flex: 1;
`;

const StatLabel = styled.div`
  font-size: 0.9rem;
  color: ${props => props.theme.colors.textLight};
  margin-bottom: ${props => props.theme.spacing.xs};
`;

const StatValue = styled.div`
  font-size: 2rem;
  font-weight: 700;
  color: ${props => props.theme.colors.text};
`;

const Section = styled.div`
  background: white;
  border-radius: ${props => props.theme.borderRadius.lg};
  box-shadow: ${props => props.theme.shadows.md};
  padding: ${props => props.theme.spacing.xl};
  margin-bottom: ${props => props.theme.spacing.xl};
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${props => props.theme.spacing.lg};
  padding-bottom: ${props => props.theme.spacing.md};
  border-bottom: 2px solid ${props => props.theme.colors.border};
`;

const SectionTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  color: ${props => props.theme.colors.text};
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Thead = styled.thead`
  background: ${props => props.theme.colors.background};
`;

const Th = styled.th`
  padding: ${props => props.theme.spacing.md};
  text-align: left;
  font-weight: 600;
  color: ${props => props.theme.colors.text};
  border-bottom: 2px solid ${props => props.theme.colors.border};
`;

const Tbody = styled.tbody``;

const Tr = styled.tr`
  border-bottom: 1px solid ${props => props.theme.colors.border};
  transition: background ${props => props.theme.transitions.fast};

  &:hover {
    background: ${props => props.theme.colors.background};
  }
`;

const Td = styled.td`
  padding: ${props => props.theme.spacing.md};
  color: ${props => props.theme.colors.text};
`;

const ActionButtons = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.sm};
`;

const ActionButton = styled.button<{ $variant?: 'primary' | 'danger' }>`
  padding: ${props => props.theme.spacing.xs} ${props => props.theme.spacing.md};
  background: ${props => {
    if (props.$variant === 'danger') return '#e74c3c';
    if (props.$variant === 'primary') return props.theme.colors.primary;
    return props.theme.colors.background;
  }};
  color: ${props => props.$variant ? 'white' : props.theme.colors.text};
  border: ${props => props.$variant ? 'none' : `1px solid ${props.theme.colors.border}`};
  border-radius: ${props => props.theme.borderRadius.sm};
  font-size: 0.85rem;
  cursor: pointer;
  transition: all ${props => props.theme.transitions.fast};
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.xs};

  &:hover {
    opacity: 0.8;
    transform: translateY(-1px);
  }
`;

const DeleteButton = styled.button`
  padding: ${props => props.theme.spacing.xs} ${props => props.theme.spacing.md};
  background: #e74c3c;
  color: white;
  border: none;
  border-radius: ${props => props.theme.borderRadius.sm};
  font-size: 0.85rem;
  cursor: pointer;
  transition: all ${props => props.theme.transitions.fast};
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.xs};

  &:hover {
    opacity: 0.8;
    transform: translateY(-1px);
  }
`;

const Badge = styled.span<{ $published: boolean }>`
  padding: ${props => props.theme.spacing.xs} ${props => props.theme.spacing.sm};
  background: ${props => props.$published ? '#27ae60' : '#95a5a6'};
  color: white;
  border-radius: ${props => props.theme.borderRadius.sm};
  font-size: 0.8rem;
  font-weight: 600;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${props => props.theme.spacing.xxl};
  color: ${props => props.theme.colors.textLight};
`;

const ChartContainer = styled.div`
  width: 100%;
  height: 350px;
  margin-top: ${props => props.theme.spacing.lg};
  padding: ${props => props.theme.spacing.lg};
  background: white;
  border-radius: ${props => props.theme.borderRadius.md};
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
`;

const TabContainer = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.sm};
  margin-bottom: ${props => props.theme.spacing.lg};
  border-bottom: 2px solid ${props => props.theme.colors.border};
`;

const TabButton = styled.button<{ $active: boolean }>`
  padding: ${props => props.theme.spacing.md} ${props => props.theme.spacing.lg};
  background: ${props => props.$active ? 'white' : 'transparent'};
  color: ${props => props.$active ? props.theme.colors.primary : props.theme.colors.textLight};
  border: none;
  border-bottom: 3px solid ${props => props.$active ? props.theme.colors.primary : 'transparent'};
  font-size: 0.95rem;
  font-weight: ${props => props.$active ? '600' : '400'};
  cursor: pointer;
  transition: all ${props => props.theme.transitions.fast};
  margin-bottom: -2px;

  &:hover {
    color: ${props => props.theme.colors.primary};
    background: rgba(52, 152, 219, 0.05);
  }
`;

const ToggleButtonGroup = styled.div`
  display: inline-flex;
  background: ${props => props.theme.colors.background};
  border-radius: ${props => props.theme.borderRadius.md};
  border: 1px solid ${props => props.theme.colors.border};
  overflow: hidden;
`;

const ToggleButton = styled.button<{ $active: boolean }>`
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.lg};
  background: ${props => props.$active ? props.theme.colors.primary : 'transparent'};
  color: ${props => props.$active ? 'white' : props.theme.colors.text};
  border: none;
  font-size: 0.9rem;
  font-weight: ${props => props.$active ? '600' : '400'};
  cursor: pointer;
  transition: all ${props => props.theme.transitions.fast};

  &:hover {
    background: ${props => props.$active ? props.theme.colors.primary : 'rgba(52, 152, 219, 0.1)'};
  }
`;

const EmailList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.md};
  margin-top: ${props => props.theme.spacing.lg};
`;

const EmailItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${props => props.theme.spacing.md};
  background: ${props => props.theme.colors.background};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
`;

const EmailText = styled.span`
  font-size: 1rem;
  color: ${props => props.theme.colors.text};
`;

const EmailInput = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.md};
  margin-top: ${props => props.theme.spacing.xl};
`;

const Input = styled.input`
  flex: 1;
  padding: ${props => props.theme.spacing.md};
  border: 2px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  font-size: 1rem;
  transition: border-color ${props => props.theme.transitions.fast};

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
  }
`;

const InquiryTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-top: ${props => props.theme.spacing.lg};
  background: white;
  border-radius: ${props => props.theme.borderRadius.md};
  overflow: hidden;
  box-shadow: ${props => props.theme.shadows.sm};
`;

const InquiryTh = styled.th`
  background: ${props => props.theme.colors.primary};
  color: white;
  padding: ${props => props.theme.spacing.md};
  text-align: left;
  font-weight: 600;
  font-size: 0.9rem;
`;

const InquiryTd = styled.td`
  padding: ${props => props.theme.spacing.md};
  border-bottom: 1px solid ${props => props.theme.colors.border};
  font-size: 0.9rem;
`;

const InquiryCheckbox = styled.input`
  width: 18px;
  height: 18px;
  cursor: pointer;
`;

const StatusSelect = styled.select`
  padding: ${props => props.theme.spacing.xs} ${props => props.theme.spacing.sm};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.sm};
  font-size: 0.85rem;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
  }
`;

const AdminDashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<'overview' | 'news' | 'products' | 'analytics' | 'settings' | 'inquiries'>('overview');
  const [newsData, setNewsData] = useState<News[]>([]);
  const [showEditor, setShowEditor] = useState(false);
  const [editingNews, setEditingNews] = useState<News | undefined>(undefined);
  const [analyticsData, setAnalyticsData] = useState<AnalyticsOverview | null>(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [analyticsSubTab, setAnalyticsSubTab] = useState<'pages' | 'countries' | 'devices'>('pages');
  const [timeView, setTimeView] = useState<'daily' | 'monthly'>('daily');

  // Settings state
  const [adminEmails, setAdminEmails] = useState<string[]>([]);
  const [newEmail, setNewEmail] = useState('');
  const [emailsLoading, setEmailsLoading] = useState(false);

  // Inquiries state
  const [inquiries, setInquiries] = useState<ContactInquiry[]>([]);
  const [selectedInquiries, setSelectedInquiries] = useState<Set<number>>(new Set());
  const [inquiriesLoading, setInquiriesLoading] = useState(false);
  const [inquiryStatusFilter, setInquiryStatusFilter] = useState<'all' | 'NEW' | 'IN_PROGRESS' | 'COMPLETED'>('all');

  // 차트 색상 팔레트
  const CHART_COLORS = ['#3498db', '#2ecc71', '#f39c12', '#e74c3c', '#9b59b6', '#1abc9c', '#34495e'];

  // 인증 확인
  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (!token) {
      navigate('/');
    }
  }, [navigate]);

  // 뉴스 데이터 가져오기 (published 제거 - 모든 뉴스 표시)
  const fetchNews = async () => {
    try {
      const data = await getNews({ lang: 'ko' });
      setNewsData(data);
    } catch (err) {
      console.error('Failed to fetch news:', err);
    }
  };

  // Analytics 데이터 가져오기
  const fetchAnalytics = async () => {
    setAnalyticsLoading(true);
    try {
      const data = await getAnalyticsOverview();
      setAnalyticsData(data);
    } catch (err) {
      console.error('Failed to fetch analytics:', err);
    } finally {
      setAnalyticsLoading(false);
    }
  };

  // Fetch admin emails
  const fetchAdminEmails = async () => {
    try {
      setEmailsLoading(true);
      const token = localStorage.getItem('adminToken') || '';
      const emails = await getAdminEmails(token);
      setAdminEmails(emails);
    } catch (err) {
      console.error('Failed to fetch admin emails:', err);
    } finally {
      setEmailsLoading(false);
    }
  };

  // Fetch inquiries
  const fetchInquiries = async () => {
    try {
      setInquiriesLoading(true);
      const token = localStorage.getItem('adminToken') || '';
      const data = await getAllInquiries(token);
      setInquiries(data);
    } catch (err) {
      console.error('Failed to fetch inquiries:', err);
    } finally {
      setInquiriesLoading(false);
    }
  };

  useEffect(() => {
    fetchNews();
  }, []);

  // Fetch data when tabs are activated
  useEffect(() => {
    if (activeTab === 'analytics') {
      fetchAnalytics();
    } else if (activeTab === 'settings') {
      fetchAdminEmails();
    } else if (activeTab === 'inquiries') {
      fetchInquiries();
    }
  }, [activeTab]);

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminUsername');
    navigate('/');
  };

  const handleCreateNews = () => {
    setEditingNews(undefined);
    setShowEditor(true);
  };

  const handleEditNews = (news: News) => {
    setEditingNews(news);
    setShowEditor(true);
  };

  const handleEditorSave = async () => {
    setShowEditor(false);
    setEditingNews(undefined);
    await fetchNews();
    alert('저장되었습니다!');
  };

  const handleEditorCancel = () => {
    setShowEditor(false);
    setEditingNews(undefined);
  };

  const handleDeleteNews = async (id: number) => {
    if (window.confirm(t('confirm.deleteNews'))) {
      try {
        const token = localStorage.getItem('adminToken');
        if (!token) {
          alert(t('error.noAuthToken'));
          return;
        }

        const success = await deleteNewsApi(id, token);
        if (success) {
          setNewsData(newsData.filter(news => news.id !== id));
          alert(t('success.deleted'));
        } else {
          alert(t('error.deleteFailed'));
        }
      } catch (err) {
        console.error('Failed to delete news:', err);
        alert(t('error.deleteError'));
      }
    }
  };

  // Settings handlers
  const handleAddEmail = async () => {
    if (!newEmail.trim()) {
      alert('이메일 주소를 입력해주세요.');
      return;
    }

    // Simple email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newEmail)) {
      alert('올바른 이메일 형식이 아닙니다.');
      return;
    }

    try {
      const token = localStorage.getItem('adminToken') || '';
      const updatedEmails = await addAdminEmail(newEmail, token);
      setAdminEmails(updatedEmails);
      setNewEmail('');
      alert('이메일이 추가되었습니다.');
    } catch (err: any) {
      console.error('Failed to add email:', err);
      alert(err.message || '이메일 추가에 실패했습니다.');
    }
  };

  const handleRemoveEmail = async (email: string) => {
    if (!window.confirm(`${email}을(를) 삭제하시겠습니까?`)) {
      return;
    }

    try {
      const token = localStorage.getItem('adminToken') || '';
      const updatedEmails = await removeAdminEmail(email, token);
      setAdminEmails(updatedEmails);
      alert('이메일이 삭제되었습니다.');
    } catch (err: any) {
      console.error('Failed to remove email:', err);
      alert(err.message || '이메일 삭제에 실패했습니다.');
    }
  };

  // Inquiries handlers
  const handleToggleInquirySelection = (id: number) => {
    const newSelection = new Set(selectedInquiries);
    if (newSelection.has(id)) {
      newSelection.delete(id);
    } else {
      newSelection.add(id);
    }
    setSelectedInquiries(newSelection);
  };

  const handleToggleAllInquiries = () => {
    // 현재 필터링된 목록 계산
    const currentFiltered = inquiryStatusFilter === 'all'
      ? inquiries
      : inquiries.filter(i => i.status === inquiryStatusFilter);

    const filteredIds = currentFiltered.map(i => i.id);
    const allFilteredSelected = filteredIds.every(id => selectedInquiries.has(id));

    if (allFilteredSelected && filteredIds.length > 0) {
      // 현재 필터의 모든 항목이 선택되어 있으면 선택 해제
      const newSelection = new Set(selectedInquiries);
      filteredIds.forEach(id => newSelection.delete(id));
      setSelectedInquiries(newSelection);
    } else {
      // 현재 필터의 항목들을 추가 선택
      const newSelection = new Set(selectedInquiries);
      filteredIds.forEach(id => newSelection.add(id));
      setSelectedInquiries(newSelection);
    }
  };

  const handleExportCSV = () => {
    if (selectedInquiries.size === 0) {
      alert('내보낼 문의를 선택해주세요.');
      return;
    }

    const selectedData = inquiries.filter(i => selectedInquiries.has(i.id));

    // CSV 헤더
    const headers = ['ID', '이름', '이메일', '연락처', '제목', '내용', '상태', '생성일시'];

    // CSV 행
    const rows = selectedData.map(inquiry => [
      inquiry.id,
      inquiry.name,
      inquiry.email,
      inquiry.phone || '',
      inquiry.subject || '',
      `"${inquiry.message.replace(/"/g, '""')}"`, // 내용에 쉼표나 따옴표가 있을 수 있으므로 이스케이프
      inquiry.status,
      new Date(inquiry.createdAt).toLocaleString('ko-KR')
    ]);

    // CSV 문자열 생성
    const csvContent = [headers, ...rows]
      .map(row => row.join(','))
      .join('\n');

    // BOM 추가 (한글 깨짐 방지)
    const BOM = '\uFEFF';
    const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);

    // 다운로드
    const link = document.createElement('a');
    link.href = url;
    link.download = `inquiries_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(url);

    alert(`${selectedInquiries.size}개의 문의가 CSV로 내보내졌습니다.`);
  };

  const handleExportExcel = () => {
    if (selectedInquiries.size === 0) {
      alert('내보낼 문의를 선택해주세요.');
      return;
    }

    const selectedData = inquiries.filter(i => selectedInquiries.has(i.id));

    // Excel 데이터 준비
    const excelData = selectedData.map(inquiry => ({
      'ID': inquiry.id,
      '이름': inquiry.name,
      '이메일': inquiry.email,
      '연락처': inquiry.phone || '',
      '제목': inquiry.subject || '',
      '내용': inquiry.message,
      '상태': inquiry.status === 'NEW' ? '신규' : inquiry.status === 'IN_PROGRESS' ? '진행중' : '완료',
      '생성일시': new Date(inquiry.createdAt).toLocaleString('ko-KR')
    }));

    // 워크시트 생성
    const ws = XLSX.utils.json_to_sheet(excelData);

    // 열 너비 자동 조정
    const colWidths = [
      { wch: 5 },   // ID
      { wch: 10 },  // 이름
      { wch: 25 },  // 이메일
      { wch: 15 },  // 연락처
      { wch: 30 },  // 제목
      { wch: 50 },  // 내용
      { wch: 10 },  // 상태
      { wch: 20 }   // 생성일시
    ];
    ws['!cols'] = colWidths;

    // 워크북 생성
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, '고객문의');

    // 파일 다운로드
    XLSX.writeFile(wb, `inquiries_${new Date().toISOString().split('T')[0]}.xlsx`);

    alert(`${selectedInquiries.size}개의 문의가 Excel로 내보내졌습니다.`);
  };

  const handleUpdateInquiryStatus = async (id: number, status: 'NEW' | 'IN_PROGRESS' | 'COMPLETED', adminNote: string = '') => {
    try {
      const token = localStorage.getItem('adminToken') || '';
      const updated = await updateInquiryStatus(id, status, adminNote, token);
      setInquiries(inquiries.map(i => i.id === id ? updated : i));
      alert('상태가 업데이트되었습니다.');
    } catch (err: any) {
      console.error('Failed to update inquiry status:', err);
      alert(err.message || '상태 업데이트에 실패했습니다.');
    }
  };

  const handleDeleteInquiry = async (id: number) => {
    if (!window.confirm('이 문의를 삭제하시겠습니까?')) {
      return;
    }

    try {
      const token = localStorage.getItem('adminToken') || '';
      await deleteInquiry(id, token);
      setInquiries(inquiries.filter(i => i.id !== id));
      setSelectedInquiries(prev => {
        const newSet = new Set(prev);
        newSet.delete(id);
        return newSet;
      });
      alert('문의가 삭제되었습니다.');
    } catch (err: any) {
      console.error('Failed to delete inquiry:', err);
      alert(err.message || '문의 삭제에 실패했습니다.');
    }
  };

  const publishedNews = newsData.filter(n => n.isPublished);
  const draftNews = newsData.filter(n => !n.isPublished);

  // 필터링된 문의 목록
  const filteredInquiries = inquiryStatusFilter === 'all'
    ? inquiries
    : inquiries.filter(i => i.status === inquiryStatusFilter);

  // 문의 상태별 카운트
  const inquiryCounts = {
    all: inquiries.length,
    NEW: inquiries.filter(i => i.status === 'NEW').length,
    IN_PROGRESS: inquiries.filter(i => i.status === 'IN_PROGRESS').length,
    COMPLETED: inquiries.filter(i => i.status === 'COMPLETED').length,
  };

  return (
    <Container>
      <Sidebar>
        <Logo>나우바이오 관리자</Logo>
        <NavMenu>
          <NavItem $active={activeTab === 'overview'} onClick={() => setActiveTab('overview')}>
            <FaChartLine />
            대시보드
          </NavItem>
          <NavItem $active={activeTab === 'analytics'} onClick={() => setActiveTab('analytics')}>
            <FaChartBar />
            사이트 분석
          </NavItem>
          <NavItem $active={activeTab === 'news'} onClick={() => setActiveTab('news')}>
            <FaNewspaper />
            뉴스 관리
          </NavItem>
          <NavItem $active={activeTab === 'products'} onClick={() => setActiveTab('products')}>
            <FaBox />
            제품 관리
          </NavItem>
          <NavItem $active={activeTab === 'inquiries'} onClick={() => setActiveTab('inquiries')}>
            <FaEnvelope />
            고객 문의
          </NavItem>
          <NavItem $active={activeTab === 'settings'} onClick={() => setActiveTab('settings')}>
            <FaCog />
            설정
          </NavItem>
        </NavMenu>
        <LogoutButton onClick={handleLogout}>
          <FaSignOutAlt />
          로그아웃
        </LogoutButton>
      </Sidebar>

      <MainContent>
        {activeTab === 'overview' && (
          <>
            <Header>
              <Title>대시보드</Title>
              <Subtitle>나우바이오 관리자 페이지에 오신 것을 환영합니다.</Subtitle>
            </Header>

            <StatsGrid>
              <StatCard>
                <StatIcon $color="#3498db">
                  <FaNewspaper />
                </StatIcon>
                <StatInfo>
                  <StatLabel>전체 뉴스</StatLabel>
                  <StatValue>{newsData.length}</StatValue>
                </StatInfo>
              </StatCard>

              <StatCard>
                <StatIcon $color="#27ae60">
                  <FaEye />
                </StatIcon>
                <StatInfo>
                  <StatLabel>게시된 뉴스</StatLabel>
                  <StatValue>{publishedNews.length}</StatValue>
                </StatInfo>
              </StatCard>

              <StatCard>
                <StatIcon $color="#f39c12">
                  <FaBox />
                </StatIcon>
                <StatInfo>
                  <StatLabel>임시 저장</StatLabel>
                  <StatValue>{draftNews.length}</StatValue>
                </StatInfo>
              </StatCard>
            </StatsGrid>

            <Section>
              <SectionHeader>
                <SectionTitle>최근 뉴스</SectionTitle>
              </SectionHeader>
              {newsData.length > 0 ? (
                <Table>
                  <Thead>
                    <tr>
                      <Th>제목</Th>
                      <Th>카테고리</Th>
                      <Th>상태</Th>
                      <Th>날짜</Th>
                    </tr>
                  </Thead>
                  <Tbody>
                    {newsData.slice(0, 5).map(news => (
                      <Tr key={news.id}>
                        <Td>{news.title}</Td>
                        <Td>{news.category}</Td>
                        <Td>
                          <Badge $published={news.isPublished}>
                            {news.isPublished ? '게시됨' : '임시저장'}
                          </Badge>
                        </Td>
                        <Td>{news.publishedAt}</Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              ) : (
                <EmptyState>데이터가 없습니다.</EmptyState>
              )}
            </Section>
          </>
        )}

        {activeTab === 'news' && (
          <>
            {showEditor ? (
              <NewsEditor
                newsItem={editingNews}
                onSave={handleEditorSave}
                onCancel={handleEditorCancel}
              />
            ) : (
              <>
                <Header>
                  <Title>뉴스 관리</Title>
                  <Subtitle>뉴스를 생성, 수정, 삭제할 수 있습니다.</Subtitle>
                </Header>

                <Section>
                  <SectionHeader>
                    <SectionTitle>전체 뉴스 ({newsData.length}개)</SectionTitle>
                    <ActionButton $variant="primary" onClick={handleCreateNews}>
                      <FaPlus />
                      새 뉴스 작성
                    </ActionButton>
                  </SectionHeader>
              {newsData.length > 0 ? (
                <Table>
                  <Thead>
                    <tr>
                      <Th>ID</Th>
                      <Th>제목</Th>
                      <Th>카테고리</Th>
                      <Th>상태</Th>
                      <Th>날짜</Th>
                      <Th>작업</Th>
                    </tr>
                  </Thead>
                  <Tbody>
                    {newsData.map(news => (
                      <Tr key={news.id}>
                        <Td>{news.id}</Td>
                        <Td>{news.title}</Td>
                        <Td>{news.category}</Td>
                        <Td>
                          <Badge $published={news.isPublished}>
                            {news.isPublished ? '게시됨' : '임시저장'}
                          </Badge>
                        </Td>
                        <Td>{news.publishedAt}</Td>
                        <Td>
                          <ActionButtons>
                            <ActionButton onClick={() => navigate(`/news/${news.id}`)}>
                              <FaEye />
                              보기
                            </ActionButton>
                            <ActionButton $variant="primary" onClick={() => handleEditNews(news)}>
                              <FaEdit />
                              수정
                            </ActionButton>
                            <ActionButton $variant="danger" onClick={() => handleDeleteNews(news.id)}>
                              <FaTrash />
                              삭제
                            </ActionButton>
                          </ActionButtons>
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
                ) : (
                  <EmptyState>뉴스 데이터가 없습니다.</EmptyState>
                )}
              </Section>
            </>
          )}
          </>
        )}

        {activeTab === 'products' && (
          <>
            <Header>
              <Title>제품 관리</Title>
              <Subtitle>제품을 생성, 수정, 삭제할 수 있습니다.</Subtitle>
            </Header>

            <Section>
              <EmptyState>제품 관리 기능은 곧 추가될 예정입니다.</EmptyState>
            </Section>
          </>
        )}

        {activeTab === 'analytics' && (
          <>
            <Header>
              <Title>사이트 분석</Title>
              <Subtitle>자체 Analytics 기반 사이트 방문 현황을 확인할 수 있습니다.</Subtitle>
            </Header>

            {analyticsLoading ? (
              <Section>
                <EmptyState>분석 데이터를 불러오는 중...</EmptyState>
              </Section>
            ) : analyticsData ? (
              <>
                <StatsGrid>
                  <StatCard>
                    <StatIcon $color="#3498db">
                      <FaEye />
                    </StatIcon>
                    <StatInfo>
                      <StatLabel>오늘 페이지뷰</StatLabel>
                      <StatValue>{analyticsData.todayPageViews.toLocaleString()}</StatValue>
                    </StatInfo>
                  </StatCard>

                  <StatCard>
                    <StatIcon $color="#27ae60">
                      <FaUsers />
                    </StatIcon>
                    <StatInfo>
                      <StatLabel>오늘 방문자</StatLabel>
                      <StatValue>{analyticsData.todayVisitors.toLocaleString()}</StatValue>
                    </StatInfo>
                  </StatCard>

                  <StatCard>
                    <StatIcon $color="#f39c12">
                      <FaEye />
                    </StatIcon>
                    <StatInfo>
                      <StatLabel>전체 페이지뷰</StatLabel>
                      <StatValue>{analyticsData.totalPageViews.toLocaleString()}</StatValue>
                    </StatInfo>
                  </StatCard>

                  <StatCard>
                    <StatIcon $color="#9b59b6">
                      <FaUsers />
                    </StatIcon>
                    <StatInfo>
                      <StatLabel>전체 방문자</StatLabel>
                      <StatValue>{analyticsData.totalVisitors.toLocaleString()}</StatValue>
                    </StatInfo>
                  </StatCard>
                </StatsGrid>

                {/* 방문 추이 - 꺾은선 그래프 */}
                <Section>
                  <SectionHeader>
                    <SectionTitle>
                      {timeView === 'daily' ? '일별 방문 추이 (최근 30일)' : '월별 방문 추이 (최근 12개월)'}
                    </SectionTitle>
                    <ToggleButtonGroup>
                      <ToggleButton
                        $active={timeView === 'daily'}
                        onClick={() => setTimeView('daily')}
                      >
                        일별
                      </ToggleButton>
                      <ToggleButton
                        $active={timeView === 'monthly'}
                        onClick={() => setTimeView('monthly')}
                      >
                        월별
                      </ToggleButton>
                    </ToggleButtonGroup>
                  </SectionHeader>
                  {timeView === 'daily' && analyticsData.dailyStats && analyticsData.dailyStats.length > 0 ? (
                    <ChartContainer style={{ height: '400px' }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={analyticsData.dailyStats}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis
                            dataKey="date"
                            tickFormatter={(value) => {
                              const date = new Date(value);
                              return `${date.getMonth() + 1}/${date.getDate()}`;
                            }}
                          />
                          <YAxis />
                          <Tooltip
                            labelFormatter={(value) => `날짜: ${value}`}
                          />
                          <Legend />
                          <Line
                            type="monotone"
                            dataKey="pageViews"
                            stroke="#3498db"
                            strokeWidth={2}
                            name="페이지뷰"
                            dot={{ r: 4 }}
                            activeDot={{ r: 6 }}
                          />
                          <Line
                            type="monotone"
                            dataKey="visitors"
                            stroke="#2ecc71"
                            strokeWidth={2}
                            name="방문자"
                            dot={{ r: 4 }}
                            activeDot={{ r: 6 }}
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </ChartContainer>
                  ) : timeView === 'monthly' && analyticsData.monthlyStats && analyticsData.monthlyStats.length > 0 ? (
                    <ChartContainer style={{ height: '400px' }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={analyticsData.monthlyStats}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis
                            dataKey="month"
                            tickFormatter={(value) => {
                              const [year, month] = value.split('-');
                              return `${year}년 ${parseInt(month)}월`;
                            }}
                          />
                          <YAxis />
                          <Tooltip
                            labelFormatter={(value) => {
                              const [year, month] = value.split('-');
                              return `${year}년 ${parseInt(month)}월`;
                            }}
                          />
                          <Legend />
                          <Line
                            type="monotone"
                            dataKey="pageViews"
                            stroke="#3498db"
                            strokeWidth={2}
                            name="페이지뷰"
                            dot={{ r: 4 }}
                            activeDot={{ r: 6 }}
                          />
                          <Line
                            type="monotone"
                            dataKey="visitors"
                            stroke="#2ecc71"
                            strokeWidth={2}
                            name="방문자"
                            dot={{ r: 4 }}
                            activeDot={{ r: 6 }}
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </ChartContainer>
                  ) : (
                    <EmptyState>통계 데이터가 없습니다.</EmptyState>
                  )}
                </Section>

                {/* 세부 분석 탭 */}
                <Section>
                  <TabContainer>
                    <TabButton
                      $active={analyticsSubTab === 'pages'}
                      onClick={() => setAnalyticsSubTab('pages')}
                    >
                      인기 페이지
                    </TabButton>
                    <TabButton
                      $active={analyticsSubTab === 'countries'}
                      onClick={() => setAnalyticsSubTab('countries')}
                    >
                      국가별 방문자
                    </TabButton>
                    <TabButton
                      $active={analyticsSubTab === 'devices'}
                      onClick={() => setAnalyticsSubTab('devices')}
                    >
                      디바이스별 비율
                    </TabButton>
                  </TabContainer>

                  {analyticsSubTab === 'pages' && (
                    <>
                      {analyticsData.popularPages && analyticsData.popularPages.length > 0 ? (
                        <ChartContainer>
                          <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={analyticsData.popularPages}>
                              <CartesianGrid strokeDasharray="3 3" />
                              <XAxis dataKey="path" />
                              <YAxis />
                              <Tooltip />
                              <Legend />
                              <Bar dataKey="views" fill="#3498db" name="조회수" />
                            </BarChart>
                          </ResponsiveContainer>
                        </ChartContainer>
                      ) : (
                        <EmptyState>인기 페이지 데이터가 없습니다.</EmptyState>
                      )}
                    </>
                  )}

                  {analyticsSubTab === 'countries' && (
                    <>
                      {analyticsData.countriesData && analyticsData.countriesData.length > 0 ? (
                        <ChartContainer>
                          <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={analyticsData.countriesData}>
                              <CartesianGrid strokeDasharray="3 3" />
                              <XAxis dataKey="country" />
                              <YAxis />
                              <Tooltip />
                              <Legend />
                              <Bar dataKey="visitors" fill="#2ecc71" name="방문자 수" />
                            </BarChart>
                          </ResponsiveContainer>
                        </ChartContainer>
                      ) : (
                        <EmptyState>국가별 방문자 데이터가 없습니다.</EmptyState>
                      )}
                    </>
                  )}

                  {analyticsSubTab === 'devices' && (
                    <>
                      {analyticsData.devicesData && analyticsData.devicesData.length > 0 ? (
                        <ChartContainer>
                          <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                              <Pie
                                data={analyticsData.devicesData}
                                dataKey="percentage"
                                nameKey="device"
                                cx="50%"
                                cy="50%"
                                outerRadius={100}
                                label={(entry) => `${entry.device}: ${entry.percentage}%`}
                              >
                                {analyticsData.devicesData.map((entry, index) => (
                                  <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                                ))}
                              </Pie>
                              <Tooltip />
                              <Legend />
                            </PieChart>
                          </ResponsiveContainer>
                        </ChartContainer>
                      ) : (
                        <EmptyState>디바이스 데이터가 없습니다.</EmptyState>
                      )}
                    </>
                  )}
                </Section>
              </>
            ) : (
              <Section>
                <EmptyState>분석 데이터를 불러오지 못했습니다.</EmptyState>
              </Section>
            )}
          </>
        )}

        {/* 설정 탭 */}
        {activeTab === 'settings' && (
          <>
            <Header>
              <Title>설정</Title>
            </Header>

            <Section>
              <SectionTitle>관리자 이메일 관리</SectionTitle>
              <p style={{ color: '#666', marginBottom: '1rem' }}>
                고객 문의가 접수되면 아래 이메일 주소로 알림이 발송됩니다.
              </p>

              {emailsLoading ? (
                <EmptyState>로딩 중...</EmptyState>
              ) : (
                <>
                  {adminEmails.length === 0 ? (
                    <EmptyState>등록된 관리자 이메일이 없습니다.</EmptyState>
                  ) : (
                    <EmailList>
                      {adminEmails.map((email) => (
                        <EmailItem key={email}>
                          <EmailText>{email}</EmailText>
                          <DeleteButton onClick={() => handleRemoveEmail(email)}>
                            <FaTrash /> 삭제
                          </DeleteButton>
                        </EmailItem>
                      ))}
                    </EmailList>
                  )}

                  <EmailInput>
                    <Input
                      type="email"
                      placeholder="새 이메일 주소 입력"
                      value={newEmail}
                      onChange={(e) => setNewEmail(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleAddEmail()}
                    />
                    <ActionButton onClick={handleAddEmail}>
                      <FaPlus /> 추가
                    </ActionButton>
                  </EmailInput>
                </>
              )}
            </Section>
          </>
        )}

        {/* 고객 문의 탭 */}
        {activeTab === 'inquiries' && (
          <>
            <Header>
              <Title>고객 문의 관리</Title>
              <ActionButtons>
                <ActionButton onClick={handleExportCSV}>
                  CSV 내보내기 ({selectedInquiries.size})
                </ActionButton>
                <ActionButton onClick={handleExportExcel} $variant="primary">
                  <FaFileExcel /> Excel 내보내기 ({selectedInquiries.size})
                </ActionButton>
              </ActionButtons>
            </Header>

            <Section>
              <div style={{ marginBottom: '1.5rem' }}>
                <ToggleButtonGroup>
                  <ToggleButton
                    $active={inquiryStatusFilter === 'all'}
                    onClick={() => setInquiryStatusFilter('all')}
                  >
                    전체 ({inquiryCounts.all})
                  </ToggleButton>
                  <ToggleButton
                    $active={inquiryStatusFilter === 'NEW'}
                    onClick={() => setInquiryStatusFilter('NEW')}
                  >
                    신규 ({inquiryCounts.NEW})
                  </ToggleButton>
                  <ToggleButton
                    $active={inquiryStatusFilter === 'IN_PROGRESS'}
                    onClick={() => setInquiryStatusFilter('IN_PROGRESS')}
                  >
                    진행중 ({inquiryCounts.IN_PROGRESS})
                  </ToggleButton>
                  <ToggleButton
                    $active={inquiryStatusFilter === 'COMPLETED'}
                    onClick={() => setInquiryStatusFilter('COMPLETED')}
                  >
                    완료 ({inquiryCounts.COMPLETED})
                  </ToggleButton>
                </ToggleButtonGroup>
              </div>
            </Section>

            <Section>
              {inquiriesLoading ? (
                <EmptyState>로딩 중...</EmptyState>
              ) : filteredInquiries.length === 0 ? (
                <EmptyState>
                  {inquiryStatusFilter === 'all' ? '등록된 문의가 없습니다.' : `${inquiryStatusFilter === 'NEW' ? '신규' : inquiryStatusFilter === 'IN_PROGRESS' ? '진행중' : '완료'} 상태의 문의가 없습니다.`}
                </EmptyState>
              ) : (
                <InquiryTable>
                  <thead>
                    <tr>
                      <InquiryTh style={{ width: '40px' }}>
                        <InquiryCheckbox
                          type="checkbox"
                          checked={filteredInquiries.length > 0 && filteredInquiries.every(i => selectedInquiries.has(i.id))}
                          onChange={handleToggleAllInquiries}
                        />
                      </InquiryTh>
                      <InquiryTh>ID</InquiryTh>
                      <InquiryTh>이름</InquiryTh>
                      <InquiryTh>이메일</InquiryTh>
                      <InquiryTh>연락처</InquiryTh>
                      <InquiryTh>제목</InquiryTh>
                      <InquiryTh>상태</InquiryTh>
                      <InquiryTh>생성일시</InquiryTh>
                      <InquiryTh>작업</InquiryTh>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredInquiries.map((inquiry) => (
                      <tr key={inquiry.id}>
                        <InquiryTd>
                          <InquiryCheckbox
                            type="checkbox"
                            checked={selectedInquiries.has(inquiry.id)}
                            onChange={() => handleToggleInquirySelection(inquiry.id)}
                          />
                        </InquiryTd>
                        <InquiryTd>{inquiry.id}</InquiryTd>
                        <InquiryTd>{inquiry.name}</InquiryTd>
                        <InquiryTd>{inquiry.email}</InquiryTd>
                        <InquiryTd>{inquiry.phone || '-'}</InquiryTd>
                        <InquiryTd>{inquiry.subject || '-'}</InquiryTd>
                        <InquiryTd>
                          <StatusSelect
                            value={inquiry.status}
                            onChange={(e) =>
                              handleUpdateInquiryStatus(
                                inquiry.id,
                                e.target.value as 'NEW' | 'IN_PROGRESS' | 'COMPLETED'
                              )
                            }
                          >
                            <option value="NEW">신규</option>
                            <option value="IN_PROGRESS">진행중</option>
                            <option value="COMPLETED">완료</option>
                          </StatusSelect>
                        </InquiryTd>
                        <InquiryTd>{new Date(inquiry.createdAt).toLocaleString('ko-KR')}</InquiryTd>
                        <InquiryTd>
                          <DeleteButton onClick={() => handleDeleteInquiry(inquiry.id)}>
                            <FaTrash />
                          </DeleteButton>
                        </InquiryTd>
                      </tr>
                    ))}
                  </tbody>
                </InquiryTable>
              )}
            </Section>
          </>
        )}
      </MainContent>
    </Container>
  );
};

export default AdminDashboardPage;
